import {
  SECTION_BG_TYPE_VALUES,
  SECTION_BG_SIZE_VALUES,
  SECTION_VERTICAL_ALIGN_VALUES,
  type BlockStyle,
  type SectionBlock,
  type Overflow,
  OVERFLOW_VALUES,
} from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';
import { isCssBackgroundImage } from '../../helpers';
import { BackgroundGradientField } from '../ui/BackgroundGradientField';
import VariableInfusedTextEditor, {
  type RichTextEditorHandle,
} from '../tiptap/VariableInfusedTextEditor';
import { useRef } from 'react';
import { Field } from '../ui/Field';
import { ImageUploader } from '../ImageUploader';
import { Switch } from '../ui/Switch';

interface SectionInspectorProps {
  block: SectionBlock;
  path: DragPath;

  variableNameOptions?: string[];
}

const BACKGROUND_POSITION_OPTIONS = [
  'center center',
  'top center',
  'bottom center',
  'center left',
  'center right',
  'top left',
  'top right',
  'bottom left',
  'bottom right',
] as const;

function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function SectionInspector({ block, path, variableNameOptions = [] }: SectionInspectorProps) {
  const richTextRef = useRef<RichTextEditorHandle>(null);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;

  const set = (patch: Partial<SectionBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  const setStyle = (patch: Partial<BlockStyle>) => {
    updateBlock(block.id, { style: { ...block.style, ...patch } }, path);
  };

  const gradientValue =
    block.style?.backgroundGradient ??
    (isCssBackgroundImage(attrs.backgroundSrc) ? attrs.backgroundSrc : undefined) ??
    '';

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="font-medium">Background</div>

        <div className="space-y-3">
          <Field label="Type">
            <select
              className="field-input"
              value={attrs.backgroundType}
              onChange={(e) =>
                set({ backgroundType: e.target.value as SectionBlock['attrs']['backgroundType'] })
              }
            >
              {SECTION_BG_TYPE_VALUES.map((t) => (
                <option value={t} key={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>

          {attrs.backgroundType === 'image' && (
            <>
              <Field label="Image Url">
                <VariableInfusedTextEditor
                  value={attrs.backgroundSrc || ''}
                  onChange={(val, srcIsMustache) =>
                    set({ backgroundSrc: val, backgroundSrcIsMustache: srcIsMustache })
                  }
                  placeholder="Add a url... Use @ to reference a variable"
                  options={variableNameOptions}
                  ref={richTextRef}
                  tagsOnly={true}
                  maxTags={1}
                />
              </Field>
              {/** Add image uploader button */}
              <ImageUploader
                onSuccess={(url) => set({ backgroundSrc: url, backgroundSrcIsMustache: false })}
                variables={{
                  skipOptimize: block.attrs.skipOptimize ? '1' : '0',
                }}
                mode="button"
              />
              <div>
                <Switch
                  checked={attrs.skipOptimize}
                  label="Do not convert to webp"
                  onChange={(checked) => set({ skipOptimize: checked })}
                />
              </div>

              <Field label="Position">
                <select
                  className="field-input"
                  value={attrs.backgroundPosition}
                  onChange={(e) => set({ backgroundPosition: e.target.value })}
                >
                  {BACKGROUND_POSITION_OPTIONS.map((p) => (
                    <option value={p} key={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Size">
                <select
                  className="field-input"
                  value={attrs.backgroundSize}
                  onChange={(e) =>
                    set({
                      backgroundSize: e.target.value as SectionBlock['attrs']['backgroundSize'],
                    })
                  }
                >
                  {SECTION_BG_SIZE_VALUES.map((s) => (
                    <option value={s} key={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <CheckboxField
                label="Fixed (parallax)"
                checked={attrs.backgroundFixed}
                onChange={(checked) => set({ backgroundFixed: checked })}
              />
            </>
          )}

          {attrs.backgroundType === 'gradient' && (
            <BackgroundGradientField
              id="section-bg-gradient"
              label="Gradient"
              value={gradientValue}
              onChange={(backgroundGradient) => setStyle({ backgroundGradient })}
              hint={
                block.style?.backgroundColor
                  ? 'Email builds reject gradient sections — set the Type to "color" or "image" for those.'
                  : 'Set a Background colour too: it is the solid fallback, and email builds reject a gradient section without one.'
              }
            />
          )}

          <Field label="Overlay Color" hint='e.g. "rgba(0,0,0,0.45)"'>
            <input
              className="field-input"
              type="text"
              value={attrs.overlayColor || ''}
              placeholder="rgba(0,0,0,0.45)"
              onChange={(e) => set({ overlayColor: e.target.value || undefined })}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-3">
        <div className="font-medium">Layout</div>

        <Field label="Vertical Align">
          <select
            className="field-input"
            value={attrs.verticalAlign}
            onChange={(e) =>
              set({
                verticalAlign: e.target.value as SectionBlock['attrs']['verticalAlign'],
              })
            }
          >
            {SECTION_VERTICAL_ALIGN_VALUES.map((v) => (
              <option value={v} key={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Padding X">
            <input
              className="field-input"
              type="text"
              value={attrs.paddingX}
              placeholder="2rem"
              onChange={(e) => set({ paddingX: e.target.value })}
            />
          </Field>

          <Field label="Padding Y">
            <input
              className="field-input"
              type="text"
              value={attrs.paddingY}
              placeholder="4rem"
              onChange={(e) => set({ paddingY: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Height">
            <input
              className="field-input"
              type="text"
              value={block.style?.height || ''}
              onChange={(e) => setStyle({ height: e.target.value })}
            />
          </Field>

          <Field label="Width">
            <input
              className="field-input"
              type="text"
              value={block.style?.width || ''}
              onChange={(e) => setStyle({ width: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-3">
        <div className="font-medium">Advanced</div>

        <Field label="Reference" hint="Optional anchor id used for links / navigation">
          <input
            className="field-input"
            type="text"
            value={attrs.reference || ''}
            placeholder="section-id"
            onChange={(e) => set({ reference: e.target.value || undefined })}
          />
        </Field>

        <Field label="Overflow">
          <select
            className="field-input"
            value={block.style?.overflow || ''}
            onChange={(e) =>
              setStyle({
                overflow: e.target.value as Overflow,
              })
            }
          >
            <option value="">Select</option>
            {OVERFLOW_VALUES.map((v) => (
              <option value={v} key={v}>
                {v}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  );
}
